import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';
import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {computeCumulative} from '../transforms';
import {ChartConfig, ChartDataPoint, SeriesConfig} from '../types';

/** Approximate seconds per granularity interval. */
const GRANULARITY_SECONDS: Partial<Record<DataGranularity, number>> = {
  H: 3600,
  '3H': 10800,
  '6H': 21600,
  D: 86400,
  W: 604800,
  M: 2592000,
};

export interface RainChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  comparisonData?: ChartDataPoint[];
  comparisonLabel?: string;
  dateTimeConfig?: DateTimeConfig;
  /** When provided, uses category x-axis instead of datetime (for analysis charts) */
  categories?: string[];
  /** Rain metric key (e.g. 'RR_1H', 'RR_3H', etc.). Defaults to 'RR_1H'. */
  metric?: string;
  /**
   * Names the series in the user's language. Required: the names are
   * messages, and English would otherwise reach every locale.
   */
  formatMessage: (descriptor: MessageDescriptor) => string;
}

export const rainChart = (options: RainChartOptions): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    height = 250,
    comparisonData,
    comparisonLabel,
    dateTimeConfig,
    categories,
    metric = 'RR_1H',
    formatMessage,
  } = options;

  const hasComparison = !!comparisonData;

  const series: SeriesConfig[] = [
    {
      id: 'rain',
      type: 'bar',
      dataKey: metric,
      name: formatMessage(messages.metrics.rain),
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'linear',
      showMarkers: false,
      barRadius: [theme.borderRadius, theme.borderRadius, 0, 0],
      zIndex: 1,
      ...(hasComparison ? {barGroupIndex: 0, barGroupCount: 2} : {}),
    },
  ];

  if (hasComparison) {
    series.push({
      id: 'rain-comparison',
      type: 'bar',
      dataKey: `${metric}_COMP`,
      name: comparisonLabel ?? formatMessage(messages.charts.comparison),
      color: theme.colors.textSecondary,
      yAxisIndex: 0,
      interpolation: 'linear',
      showMarkers: false,
      barRadius: [theme.borderRadius, theme.borderRadius, 0, 0],
      opacity: 0.5,
      zIndex: 0,
      barGroupIndex: 1,
      barGroupCount: 2,
    });
  }

  // Bar offset: center bars between tick marks (e.g. 0:30 fills 0:00–1:00)
  const granSeconds = GRANULARITY_SECONDS[granularity] ?? 0;
  const barOffset = categories ? 0 : granSeconds / 2;
  const offsetData =
    barOffset > 0
      ? data.map((p) => ({...p, timestamp: p.timestamp + barOffset}))
      : data;

  // Rain unit label for tick formatting
  const rainUnitLabel = Units.getUnitLabel(units, metric);

  const tooltipConfig = createTooltipConfig({
    timezone,
    granularity,
    metric,
    units,
    theme,
    dateTimeConfig,
    tooltipLabels: categories,
  });

  return {
    height,
    data: offsetData,
    series,
    xAxis: createXAxisConfig({
      type: categories ? 'category' : 'datetime',
      timezone,
      granularity,
      theme,
      categories,
    }),
    yAxes: [
      {
        ...createYAxisConfig({
          id: 'rain',
          metric,
          units,
          theme,
          domain: [0, 'auto'],
        }),
        ...(units.RR === Units.INCH
          ? {
              // The axis spans at least 0.05 in (about 1 mm), so a month of
              // 0.02 in days is not flattened under a 1 in axis; inches
              // need decimal ticks.
              minDomainMax: 0.05,
              allowDecimals: true,
              tickFormat: (value: number) =>
                value < 0 ? '' : `${+value.toFixed(2)} ${rainUnitLabel}`,
            }
          : {
              // At least 1 mm, so small amounts are shown proportionally,
              // with whole-millimetre ticks only.
              minDomainMax: 1,
              integerTicksOnly: true,
              tickFormat: (value: number) => {
                if (value < 0) return '';
                // Skip non-integer ticks (0.3→0, 0.6→1 would repeat)
                if (Math.abs(value - Math.round(value)) > 0.01) return '';
                return `${Math.max(0, Math.round(value))} ${rainUnitLabel}`;
              },
            }),
      },
    ],
    tooltip: {
      ...tooltipConfig,
      // Show original timestamp (before bar offset) in tooltip
      formatTimestamp:
        barOffset > 0
          ? (ts: number) => tooltipConfig.formatTimestamp(ts - barOffset)
          : tooltipConfig.formatTimestamp,
    },
    legend: createLegendConfig(series.length > 1, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};

export interface StormEventChartOptions {
  /** Already unit-converted points with `RR_RATE` and `rainMetric` keys. */
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  /** Rain accumulation metric key (e.g. 'RR_1H') to accumulate into the total series. */
  rainMetric: string;
  /** Storm start (unix seconds) — vertical reference line. */
  stormStart: number;
  /** Storm end (unix seconds) — vertical reference line. */
  stormEnd: number;
  /** Padded x-axis window start (unix seconds). */
  windowStart: number;
  /** Padded x-axis window end (unix seconds). */
  windowEnd: number;
  /** Pre-translated label for the storm-start reference line (no intl in core). */
  stormStartLabel?: string;
  /** Pre-translated label for the storm-end reference line (no intl in core). */
  stormEndLabel?: string;
  /** Pre-translated legend label for the rain-rate series. */
  rateLabel: string;
  /** Pre-translated legend label for the cumulative series. */
  cumulativeLabel: string;
  dateTimeConfig?: DateTimeConfig;
  height?: number;
}

/**
 * Storm event detail chart: instantaneous rain rate (left axis) plus a
 * running accumulation of `rainMetric` (right axis), with vertical
 * reference lines marking the storm start/end inside a padded window.
 *
 * Data must already be unit-converted upstream — summing converted values
 * is correct since both mm and inches accumulate linearly.
 */
export const stormEventChart = (
  options: StormEventChartOptions,
): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    rainMetric,
    stormStart,
    stormEnd,
    windowStart,
    windowEnd,
    stormStartLabel,
    stormEndLabel,
    rateLabel,
    cumulativeLabel,
    dateTimeConfig,
    height = 300,
  } = options;

  const chartData = computeCumulative(data, rainMetric, 'RR_CUMULATIVE');

  const rainUnitLabel = Units.getUnitLabel(units, rainMetric);
  const rainPrecision = units.RR === Units.INCH ? 2 : 1;

  const series: SeriesConfig[] = [
    {
      id: 'storm-rain-rate',
      type: 'area',
      dataKey: 'RR_RATE',
      name: rateLabel,
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      fillOpacity: 0.3,
      lineWidth: 2,
      showMarkers: false,
      zIndex: 1,
    },
    {
      id: 'storm-cumulative',
      type: 'area',
      dataKey: 'RR_CUMULATIVE',
      name: cumulativeLabel,
      color: theme.colors.info,
      yAxisIndex: 1,
      interpolation: 'monotone',
      fillOpacity: 0.15,
      lineWidth: 2,
      showMarkers: false,
      zIndex: 0,
    },
  ];

  return {
    height,
    data: chartData,
    series,
    xAxis: createXAxisConfig({
      type: 'datetime',
      timezone,
      granularity,
      theme,
      domain: [windowStart, windowEnd],
    }),
    yAxes: [
      createYAxisConfig({
        id: 'rate',
        metric: 'RR_RATE',
        units,
        theme,
        domain: [0, 'auto'],
      }),
      createYAxisConfig({
        id: 'cumulative',
        metric: rainMetric,
        units,
        theme,
        position: 'right',
        domain: [0, 'auto'],
        gridLines: false,
      }),
    ],
    referenceLines: [
      {
        type: 'vertical',
        value: stormStart,
        color: theme.colors.success,
        dashArray: '6 4',
        lineWidth: 1.5,
        label: stormStartLabel,
        labelPosition: 'start',
      },
      {
        type: 'vertical',
        value: stormEnd,
        color: theme.colors.error,
        dashArray: '6 4',
        lineWidth: 1.5,
        label: stormEndLabel,
        labelPosition: 'end',
      },
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'RR_RATE',
      units,
      theme,
      dateTimeConfig,
      customFormatters: {
        'storm-cumulative': (value: number) =>
          `${value.toFixed(rainPrecision)} ${rainUnitLabel}`,
      },
    }),
    legend: createLegendConfig(true, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};

export interface CumulativeRainChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  height?: number;
  /** X-axis / tooltip granularity. Defaults to 'D'. */
  granularity?: DataGranularity;
  /**
   * Names the series in the user's language. Required: the names are
   * messages, and English would otherwise reach every locale.
   */
  formatMessage: (descriptor: MessageDescriptor) => string;
}

/**
 * Cumulative rain chart. Expects data with a pre-computed `RR_CUMULATIVE` key.
 * Granularity controls only x-axis ticks and tooltip date format — the running
 * sum itself is computed upstream by Charts.computeCumulative.
 */
export const cumulativeRainChart = (
  options: CumulativeRainChartOptions,
): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    height = 400,
    granularity = 'D',
    formatMessage,
  } = options;

  return {
    height,
    data,
    series: [
      {
        id: 'cumulative-rain',
        type: 'area',
        dataKey: 'RR_CUMULATIVE',
        name: formatMessage(messages.reports.cumulativeRainfall),
        color: theme.colors.primary,
        yAxisIndex: 0,
        interpolation: 'monotone',
        fillOpacity: 0.3,
        lineWidth: 2,
        showMarkers: false,
        zIndex: 1,
      },
    ],
    xAxis: createXAxisConfig({
      type: 'datetime',
      timezone,
      granularity,
      theme,
    }),
    yAxes: [
      createYAxisConfig({
        id: 'rain',
        metric: 'RR_1H',
        units,
        theme,
        domain: [0, 'auto'],
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'RR_1H',
      units,
      theme,
    }),
    legend: createLegendConfig(false, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
